import {
    deleteRecord,
    getRecord,
    listRecords,
    saveRecord,
    subscribeToRecords,
} from '../data/appRecords';
import { supabase } from './supabaseClient';

const singletonCollections = new Set(['settings', 'cancel_reason', 'smsConfig', 'smtpdata']);
const activeListeners = new Map();
const makeRef = (path, constraints = {}) => ({
    __supabaseRef: true,
    path: Array.isArray(path) ? path : String(path || '').split('/').filter(Boolean),
    constraints,
});

const getRef = (value) => {
    if (value?.__supabaseRef) {
        return value;
    }
    throw new TypeError('Expected a Supabase database reference.');
};

const getAtPath = (value, segments) => segments.reduce((current, segment) => {
    if (current === null || current === undefined) return undefined;
    const key = Array.isArray(current) && /^\d+$/.test(segment) ? Number(segment) : segment;
    return current[key];
}, value);

const setAtPath = (root, segments, value) => {
    if (!segments.length) return value;
    const result = root && typeof root === 'object' ? root : {};
    let current = result;
    segments.slice(0, -1).forEach((segment, index) => {
        const next = segments[index + 1];
        const key = Array.isArray(current) && /^\d+$/.test(segment) ? Number(segment) : segment;
        if (!current[key] || typeof current[key] !== 'object') {
            current[key] = /^\d+$/.test(next) ? [] : {};
        }
        current = current[key];
    });
    const last = segments[segments.length - 1];
    current[Array.isArray(current) && /^\d+$/.test(last) ? Number(last) : last] = value;
    return result;
};

const removeAtPath = (root, segments) => {
    if (!segments.length || root === null || root === undefined) return root;
    const parent = getAtPath(root, segments.slice(0, -1));
    const last = segments[segments.length - 1];
    if (parent && typeof parent === 'object') {
        const key = Array.isArray(parent) && /^\d+$/.test(last) ? Number(last) : last;
        if (Array.isArray(parent)) parent.splice(key, 1);
        else delete parent[key];
    }
    return root;
};

const mergeAtPath = (root, segments, value) => {
    const current = getAtPath(root, segments);
    const merged = current && typeof current === 'object' && value && typeof value === 'object'
        ? { ...current, ...value }
        : value;
    return setAtPath(root, segments, merged);
};

const getDocumentLocation = (segments) => {
    const collection = segments[0];
    const singleton = singletonCollections.has(collection);
    return {
        collection,
        recordId: singleton ? 'main' : segments[1],
        nestedPath: singleton ? segments.slice(1) : segments.slice(2),
        isCollectionRoot: segments.length === 1 && !singleton,
        isSingletonRoot: segments.length === 1 && singleton,
    };
};

const readReference = async (reference) => {
    const { path, constraints } = getRef(reference);
    if (!path.length) return null;
    const location = getDocumentLocation(path);
    let value;

    if (location.isCollectionRoot) {
        value = await listRecords(location.collection, {
            filters: constraints.equalTo === undefined || !constraints.orderByChild
                ? {}
                : { [constraints.orderByChild]: constraints.equalTo },
        });
        if (constraints.orderByChild && constraints.equalTo !== undefined) {
            value = Object.fromEntries(Object.entries(value).filter(([, row]) => (
                String(getAtPath(row, constraints.orderByChild.split('/'))) === String(constraints.equalTo)
            )));
        }
        const entries = Object.entries(value);
        if (constraints.orderByKey || constraints.orderByChild || constraints.limitToLast) {
            entries.sort(([leftKey, left], [rightKey, right]) => {
                const leftValue = constraints.orderByKey ? leftKey : getAtPath(left, (constraints.orderByChild || '').split('/'));
                const rightValue = constraints.orderByKey ? rightKey : getAtPath(right, (constraints.orderByChild || '').split('/'));
                return leftValue === rightValue ? leftKey.localeCompare(rightKey) : String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true });
            });
        }
        if (constraints.limitToLast) {
            value = Object.fromEntries(entries.slice(-constraints.limitToLast));
        }
        return Object.keys(value).length ? value : null;
    }

    value = await getRecord(location.collection, location.recordId);
    if (location.nestedPath.length) {
        value = getAtPath(value, location.nestedPath);
    }
    return value === undefined ? null : value;
};

const snapshot = (reference, value) => ({
    key: getRef(reference).path.at(-1) || null,
    ref: reference,
    val: () => value === undefined ? null : value,
    exists: () => value !== null && value !== undefined,
});

export const ref = (_database, path) => makeRef(path);
export const child = (reference, childPath) => makeRef([...getRef(reference).path, ...String(childPath).split('/').filter(Boolean)], getRef(reference).constraints);
export const query = (reference, ...constraints) => {
    const merged = { ...getRef(reference).constraints };
    constraints.forEach((item) => Object.assign(merged, item));
    return makeRef(getRef(reference).path, merged);
};
export const orderByChild = (path) => ({ orderByChild: String(path) });
export const orderByKey = () => ({ orderByKey: true });
export const equalTo = (value) => ({ equalTo: value });
export const limitToLast = (limit) => ({ limitToLast: limit });

export const get = async (reference) => snapshot(reference, await readReference(reference));

export const onValue = (reference, callback, options = {}) => {
    let active = true;
    const notify = async () => {
        try {
            const value = await readReference(reference);
            if (active) callback(snapshot(reference, value));
        } catch (error) {
            console.error('Supabase subscription read failed:', error);
        }
    };

    if (options.onlyOnce) {
        notify();
        return () => { active = false; };
    }

    const { collection, isCollectionRoot, recordId } = getDocumentLocation(getRef(reference).path);
    const listenerKey = JSON.stringify([getRef(reference).path, getRef(reference).constraints]);
    const unsubscribe = subscribeToRecords(collection, (change) => {
        const changedId = change.new?.record_id || change.old?.record_id;
        if (isCollectionRoot || changedId === recordId) notify();
    });
    const listeners = activeListeners.get(listenerKey) || new Set();
    const stop = () => {
        active = false;
        unsubscribe();
        listeners.delete(stop);
        if (!listeners.size) activeListeners.delete(listenerKey);
    };
    listeners.add(stop);
    activeListeners.set(listenerKey, listeners);
    notify();
    return stop;
};

export const off = (reference) => {
    const listenerKey = JSON.stringify([getRef(reference).path, getRef(reference).constraints]);
    activeListeners.get(listenerKey)?.forEach((stop) => stop());
};

export const set = async (reference, value) => {
    const { collection, recordId, nestedPath, isCollectionRoot, isSingletonRoot } = getDocumentLocation(getRef(reference).path);
    if (isSingletonRoot) return saveRecord(collection, 'main', value);
    if (isCollectionRoot) {
        const existing = await listRecords(collection);
        const keys = Object.keys(existing);
        if (keys.length) {
            const { error } = await supabase.from('app_records').delete().eq('collection', collection).in('record_id', keys);
            if (error) throw error;
        }
        const entries = Object.entries(value || {}).map(([record_id, data]) => ({ collection, record_id, data }));
        await Promise.all(entries.map((entry) => saveRecord(entry.collection, entry.record_id, entry.data)));
        return;
    }
    if (!recordId) throw new Error('Cannot write to an empty Supabase collection path.');
    if (!nestedPath.length) return saveRecord(collection, recordId, value);
    const current = await getRecord(collection, recordId) || {};
    return saveRecord(collection, recordId, setAtPath(current, nestedPath, value));
};

export const update = async (reference, value) => {
    const { collection, recordId, nestedPath, isCollectionRoot, isSingletonRoot } = getDocumentLocation(getRef(reference).path);
    if (isSingletonRoot) {
        const current = await getRecord(collection, 'main') || {};
        return saveRecord(collection, 'main', { ...current, ...value });
    }
    if (isCollectionRoot) {
        await Promise.all(Object.entries(value || {}).map(async ([id, patch]) => {
            const current = await getRecord(collection, id) || {};
            await saveRecord(collection, id, { ...current, ...patch });
        }));
        return;
    }
    if (!recordId) throw new Error('Cannot update an empty Supabase collection path.');
    const current = await getRecord(collection, recordId) || {};
    return saveRecord(collection, recordId, mergeAtPath(current, nestedPath, value));
};

export const push = (reference, value) => {
    const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    const resultRef = child(reference, id);
    if (value === undefined) return resultRef;
    return set(resultRef, value).then(() => resultRef);
};

export const remove = async (reference) => {
    const { collection, recordId, nestedPath, isCollectionRoot, isSingletonRoot } = getDocumentLocation(getRef(reference).path);
    if (isSingletonRoot) return deleteRecord(collection, 'main');
    if (isCollectionRoot) {
        const existing = await listRecords(collection);
        const keys = Object.keys(existing);
        if (!keys.length) return;
        const { error } = await supabase.from('app_records').delete().eq('collection', collection).in('record_id', keys);
        if (error) throw error;
        return;
    }
    if (!recordId) return;
    if (!nestedPath.length) return deleteRecord(collection, recordId);
    const current = await getRecord(collection, recordId);
    if (current === null) return;
    return saveRecord(collection, recordId, removeAtPath(current, nestedPath));
};

export const runTransaction = async (reference, updater) => {
    const { collection, recordId, nestedPath } = getDocumentLocation(getRef(reference).path);
    if (!recordId || nestedPath.length) throw new Error('Transactions require a top-level record.');
    const client = supabase;
    if (!client) throw new Error('Supabase is not configured. Set the platform Supabase URL and anon key.');

    for (let attempt = 0; attempt < 5; attempt += 1) {
        const { data: current, error: readError } = await client
            .from('app_records')
            .select('data, version')
            .eq('collection', collection)
            .eq('record_id', String(recordId))
            .maybeSingle();
        if (readError) throw readError;

        const nextValue = updater(current?.data ?? null);
        if (nextValue === undefined) {
            return { committed: false, snapshot: snapshot(reference, current?.data ?? null) };
        }

        if (!current) {
            const { data, error } = await client
                .from('app_records')
                .insert({ collection, record_id: String(recordId), data: nextValue })
                .select('data')
                .maybeSingle();
            if (!error && data) return { committed: true, snapshot: snapshot(reference, data.data) };
            if (error?.code !== '23505') throw error;
        } else {
            const { data, error } = await client
                .from('app_records')
                .update({ data: nextValue, version: current.version + 1 })
                .eq('collection', collection)
                .eq('record_id', String(recordId))
                .eq('version', current.version)
                .select('data')
                .maybeSingle();
            if (error) throw error;
            if (data) return { committed: true, snapshot: snapshot(reference, data.data) };
        }
    }

    throw new Error('Supabase transaction conflicted repeatedly; retry the operation.');
};
